import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "../../i18n/I18nProvider";
import { API } from "../../lib/api";

export default function Footer() {
  const { t } = useI18n();
  const [email, setEmail] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t("footer_extra.subscribed"));
      setEmail("");
    } catch (err) {
      toast.error(t("footer_extra.could_not_subscribe"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-oj-deep text-zinc-200 overflow-hidden"
    >
      <div className="absolute inset-0 noise opacity-30 pointer-events-none" />
      <div className="container-oj relative pt-24 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2">
              <span className="font-display text-3xl font-black tracking-tighter text-white">
                o&#39;javana
              </span>
              <span className="h-2 w-2 rounded-full bg-oj-brand" />
            </div>
            <p className="mt-4 max-w-md text-zinc-300 leading-relaxed">{t("footer.tagline")}</p>

            <form onSubmit={submit} className="mt-8 max-w-md flex gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("footer.newsletter_ph")}
                data-testid="footer-newsletter-email"
                className="flex-1 bg-white/5 border border-white/15 rounded-md px-4 py-3 text-sm text-white placeholder:text-zinc-400 focus:outline-none focus:border-oj-brand"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-4 py-3 rounded-md hover:bg-[#6FBDB9] transition-colors disabled:opacity-60"
              >
                <Send className="size-4" />
                {loading ? "..." : t("footer.subscribe")}
              </button>
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold">{t("footer.solutions")}</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link className="hover:text-white" to="/solutions/pos">{t("sitemap_page.lbl_pos")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/kiosk">{t("sitemap_page.lbl_kiosk")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/waiter-app">{t("sitemap_page.lbl_waiter")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/qr-order-pay">{t("sitemap_page.lbl_qr")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/online-shop">{t("sitemap_page.lbl_shop")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/payment-terminals">{t("sitemap_page.lbl_terminals")}</Link></li>
              <li><Link className="hover:text-white" to="/solutions/gift-cards-loyalty">{t("sitemap_page.lbl_loyalty")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2" data-testid="footer-col-product">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold">{t("footer.product")}</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link className="hover:text-white" to="/offers" data-testid="footer-offers-link">{t("header.offers")}</Link></li>
              <li><Link className="hover:text-white" to="/industries">{t("sitemap_page.lbl_industries_all")}</Link></li>
              <li><Link className="hover:text-white" to="/pricing">{t("sitemap_page.lbl_pricing")}</Link></li>
              <li><Link className="hover:text-white" to="/configurator">{t("sitemap_page.lbl_configurator")}</Link></li>
              <li><Link className="hover:text-white" to="/why">{t("sitemap_page.lbl_why")}</Link></li>
              <li><Link className="hover:text-white" to="/about">{t("sitemap_page.lbl_about")}</Link></li>
              <li><Link className="hover:text-white" to="/alternatives/lightspeed">O&#39;javana vs. Lightspeed</Link></li>
              <li><Link className="hover:text-white" to="/alternatives/sumup">O&#39;javana vs. SumUp</Link></li>
              <li><Link className="hover:text-white" to="/alternatives/paymash">O&#39;javana vs. Paymash</Link></li>
              <li><Link className="hover:text-white" to="/sitemap">{t("sitemap_page.lbl_sitemap")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 font-semibold">{t("footer.get_started")}</div>
            <Link
              to="/#demo"
              data-testid="footer-demo-cta"
              className="mt-4 inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-4 py-3 rounded-md hover:bg-[#6FBDB9] transition-opacity"
            >
              {t("nav.demo")} <ArrowUpRight className="size-4" />
            </Link>
            <div className="mt-6 text-sm text-zinc-300">
              <div>{t("footer_extra.offices")}</div>
              <div className="mt-1">support@ojavana.com</div>
              <Link
                to="/contact"
                data-testid="footer-contact-cta"
                className="mt-4 inline-flex items-center gap-1.5 bg-oj-ink text-white text-sm font-semibold px-4 py-3 rounded-md hover:bg-zinc-800 transition-colors"
              >
                {t("footer_extra.contact_us")} <ArrowUpRight className="size-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-zinc-400">
          <div className="flex flex-col md:flex-row md:items-center gap-x-6 gap-y-2">
            <span>© {new Date().getFullYear()} O&#39;javana. {t("footer_extra.rights")}</span>
            <span className="text-zinc-500" data-testid="footer-credit">
              {t("footer_extra.website_by")}{" "}
              <a
                href="https://professionaldesigners.ch"
                target="_blank"
                rel="noreferrer"
                className="hover:text-white underline underline-offset-4 decoration-zinc-600 hover:decoration-white"
              >
                professionaldesigners.ch
              </a>
            </span>
          </div>
          <div className="flex gap-6">
            <Link className="hover:text-white" to="/legal/privacy">{t("footer.privacy")}</Link>
            <Link className="hover:text-white" to="/legal/terms">{t("footer.terms")}</Link>
            <Link className="hover:text-white" to="/legal/imprint">{t("footer.imprint")}</Link>
          </div>
        </div>

        <div className="mt-10 select-none pointer-events-none">
          <div className="font-display text-[clamp(80px,16vw,260px)] leading-[0.85] font-black tracking-tighter text-white/5">
            o&#39;javana
          </div>
        </div>
      </div>
    </footer>
  );
}
